import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  TextField,
  InputAdornment
} from '@mui/material';
import {
  Download as DownloadIcon,
  Visibility as ViewIcon,
  Search as SearchIcon,
  MoreVert as MoreVertIcon,
  Description as PayslipIcon,
  FilterList as FilterIcon
} from '@mui/icons-material';
// Import design system components
import { Card, CardContent, EmptyState } from '../../design-system';

// Import components
import PageHeader from '../../components/ui/PageHeader';
import MetricCard from '../../components/ui/MetricCard';

// Import stores
import usePayrollStore from '../../stores/payrollStore';
import useNotificationStore from '../../stores/notificationStore';

const PayrollPayslips = () => {
  const navigate = useNavigate();
  const { showSuccess, showError } = useNotificationStore();
  const {
    payslips,
    isLoading,
    error,
    fetchPayslips,
    downloadPayslip
  } = usePayrollStore();

  const [searchTerm, setSearchTerm] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedPayslip, setSelectedPayslip] = useState(null);

  useEffect(() => {
    fetchPayslips();
  }, []);

  const handleMenuOpen = (event, payslip) => {
    setAnchorEl(event.currentTarget);
    setSelectedPayslip(payslip);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedPayslip(null);
  };

  const handleDownload = async (payslip) => {
    try {
      await downloadPayslip(payslip._id);
      showSuccess('Payslip downloaded successfully');
    } catch (error) {
      showError('Failed to download payslip');
    }
    handleMenuClose();
  };

  const handleView = (payslip) => {
    navigate(`/payroll/payslips/${payslip._id}`);
    handleMenuClose();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid': return 'success';
      case 'pending': return 'warning';
      case 'draft': return 'default';
      default: return 'default';
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const filteredPayslips = payslips.filter(payslip =>
    payslip.employee?.user?.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    payslip.employee?.user?.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    payslip.employee?.employeeId?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Calculate metrics
  const totalPayslips = payslips.length;
  const paidPayslips = payslips.filter(p => p.status === 'paid').length;
  const pendingPayslips = payslips.filter(p => p.status === 'pending').length;
  const totalAmount = payslips.reduce((sum, p) => sum + (p.netPay || 0), 0);

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        title="Payslips"
        subtitle="View and manage employee payslips"
      />

      {/* Metrics */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Total Payslips"
            value={totalPayslips}
            icon={<PayslipIcon />}
            color="primary"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Paid"
            value={paidPayslips}
            icon={<PayslipIcon />}
            color="success"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Pending"
            value={pendingPayslips}
            icon={<PayslipIcon />}
            color="warning"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Total Amount"
            value={formatCurrency(totalAmount)}
            icon={<PayslipIcon />}
            color="info"
          />
        </Grid>
      </Grid>

      {/* Payslips List */}
      <Card>
        <CardContent>
          {/* Search and Filters */}
          <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
            <TextField
              placeholder="Search by employee name or ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                )
              }}
              sx={{ flexGrow: 1 }}
            />
            <Button
              variant="outlined"
              startIcon={<FilterIcon />}
            >
              Filters
            </Button>
          </Box>

          {/* Payslips Table */}
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Employee</TableCell>
                  <TableCell>Employee ID</TableCell>
                  <TableCell>Pay Period</TableCell>
                  <TableCell>Gross Pay</TableCell>
                  <TableCell>Net Pay</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredPayslips.map((payslip) => (
                  <TableRow key={payslip._id}>
                    <TableCell>
                      {payslip.employee?.user?.firstName} {payslip.employee?.user?.lastName}
                    </TableCell>
                    <TableCell>{payslip.employee?.employeeId}</TableCell>
                    <TableCell>
                      {payslip.payPeriod?.startDate} - {payslip.payPeriod?.endDate}
                    </TableCell>
                    <TableCell>{formatCurrency(payslip.grossPay || 0)}</TableCell>
                    <TableCell>{formatCurrency(payslip.netPay || 0)}</TableCell>
                    <TableCell>
                      <Chip
                        label={payslip.status}
                        color={getStatusColor(payslip.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton
                        onClick={(e) => handleMenuOpen(e, payslip)}
                      >
                        <MoreVertIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {filteredPayslips.length === 0 && (
            <EmptyState
              title="No payslips found"
              description={searchTerm ? 
                "No payslips match your search criteria. Try adjusting your search terms or filters." :
                "No payslips have been generated yet. Payslips will appear here after payroll runs are processed and finalized."
              }
              icon={<PayslipIcon />}
              action={!searchTerm ? (
                <Button
                  variant="contained"
                  onClick={() => navigate('/payroll/runs')}
                >
                  View Payroll Runs
                </Button>
              ) : (
                <Button
                  variant="outlined"
                  onClick={() => setSearchTerm('')}
                >
                  Clear Search
                </Button>
              )}
            />
          )}
        </CardContent>
      </Card>

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => handleView(selectedPayslip)}>
          <ListItemIcon><ViewIcon fontSize="small" /></ListItemIcon>
          <ListItemText>View Details</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleDownload(selectedPayslip)}>
          <ListItemIcon><DownloadIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Download PDF</ListItemText>
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default PayrollPayslips;